import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, RotateCcw, Search, Trash2 } from 'lucide-react';
import type { GenealogyPageDTO } from '@prism/contracts';
import { api, errorMessage } from '../api/client';
import { useBinVersionActions, useDeleteActions, useMe, useRecycleBin, type BinVersion } from '../api/hooks';
import { toast } from '../components/Toasts';
import { TableScroll } from '../components/TableScroll';
import { EmptyState } from '../components/EmptyState';
import { RecycleBinSummary } from '../components/RecycleBinSettings';
import { usedInText } from './genealogy';
import { bytes, dateTime, relative } from '../lib/format';
import { queryKeys } from '../api/queryKeys';

export function RecycleBin() {
  const me = useMe().data!;
  const [search, setSearch] = useState('');
  const q = useRecycleBin(search);
  const { restore, purge } = useDeleteActions();
  const qc = useQueryClient();
  const isAdmin = me.permissions.includes('admin');
  const items = q.data?.items ?? [];

  if (!me.permissions.includes('asset.delete')) {
    return <div className="empty"><div className="empty-inner"><h2>Recycle bin</h2><p>Your role cannot delete or restore assets.</p><Link className="btn" to="/library">Back to library</Link></div></div>;
  }

  async function empty() {
    const nv = q.data?.versions?.length ?? 0;
    if (!window.confirm(`Permanently delete all ${q.data?.total ?? 0} assets${nv ? ` and ${nv} earlier version${nv === 1 ? '' : 's'}` : ''} in the recycle bin? This cannot be undone.`)) return;
    try {
      const r = await api<{ purged: number; postponed: number; versions?: { purged: number; postponed: number } }>('/api/v1/recycle-bin/empty', { method: 'POST', json: {} });
      const versions = r.versions?.purged ? ` and ${r.versions.purged} version${r.versions.purged === 1 ? '' : 's'}` : '';
      const later = r.postponed + (r.versions?.postponed ?? 0);
      toast(later ? `Purged ${r.purged}${versions}; ${later} will be retried` : `Purged ${r.purged} asset${r.purged === 1 ? '' : 's'}${versions}`);
      await qc.invalidateQueries({ queryKey: queryKeys.recycleBins() });
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  }

  return (
    <>
      <header className="topbar">
        <Link to="/library" className="btn ghost icon" aria-label="Back to library"><ArrowLeft size={17} /></Link>
        <h1><Trash2 size={16} color="var(--accent-strong)" /> Recycle bin</h1>
        <div className="search" role="search">
          <Search size={15} />
          <input className="input" placeholder="Search deleted assets…" value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Search the recycle bin" />
        </div>
        <div className="topbar-spacer" />
        {isAdmin && (items.length > 0 || (q.data?.versions?.length ?? 0) > 0) && <button className="btn danger" onClick={() => void empty()} data-testid="empty-bin"><Trash2 size={14} /> Empty recycle bin</button>}
      </header>
      <div className="page" data-testid="recycle-bin">
        <p className="lede">
          Deleted assets wait here until their purge date. Restoring puts an asset back exactly as it was. Purging removes proxies and derivatives and deletes originals PRISM stored; files catalogued in place are never deleted.
        </p>
        {q.data && <RecycleBinSummary settings={q.data.settings} isAdmin={isAdmin} />}
        {q.isLoading ? <div className="skeleton" style={{ height: 160 }} /> : items.length === 0 ? (
          (q.data?.versions?.length ?? 0) > 0 ? null
            : <EmptyState style={{ minHeight: 200 }} icon={<Trash2 size={24} />} title="The recycle bin is empty"><p>{search ? 'Nothing deleted matches this search.' : 'Deleted assets appear here.'}</p></EmptyState>
        ) : (
          <table className="table" aria-label="Deleted assets" style={{ marginTop: 20 }}>
            <thead><tr><th style={{ width: 88 }} aria-label="Preview" /><th>Title</th><th>Deleted</th><th>Purge date</th><th>Size</th><th aria-label="Actions" /></tr></thead>
            <tbody>
              {items.map((a) => (
                <tr key={a.id} data-testid="bin-item">
                  <td>{a.thumbnailUrl ? <img className="mini-thumb" src={a.thumbnailUrl} alt="" loading="lazy" /> : <div className="mini-thumb" />}</td>
                  <td className="wrap" style={{ fontWeight: 550, minWidth: 160 }}><Link to={`/assets/${a.id}`}>{a.title}</Link><div className="faint" style={{ fontSize: 12 }}>{a.originalFilename}</div></td>
                  <td className="muted" title={dateTime(a.deleted?.at)}>{relative(a.deleted?.at)}{a.deleted?.by ? ` by ${a.deleted.by}` : ''}</td>
                  <td className="muted" title={dateTime(a.deleted?.purgeAfter)}>
                    {relative(a.deleted?.purgeAfter)}
                    {a.deleted?.purgeError && <div style={{ color: 'var(--danger)', fontSize: 12 }}>{a.deleted.purgeError}</div>}
                  </td>
                  <td className="muted">{bytes(a.sizeBytes)}</td>
                  <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                    <button className="btn sm" disabled={restore.isPending} data-testid="bin-restore"
                      onClick={() => restore.mutate(a.id, { onSuccess: () => toast(`Restored “${a.title}”`), onError: (e) => toast(errorMessage(e), 'error') })}>
                      <RotateCcw size={13} /> Restore
                    </button>{' '}
                    <button className="btn ghost sm danger" disabled={purge.isPending} data-testid="bin-purge"
                      onClick={async () => {
                        // 1.8.0: the assets made with it keep the record of that use (genealogy).
                        const n = await api<GenealogyPageDTO>(`/api/v1/assets/${a.id}/genealogy`).then((g) => g.usedInCount, () => 0);
                        const line = usedInText(n);
                        if (!window.confirm(`Delete “${a.title}” permanently? This cannot be undone.${line ? `\n\n${line}` : ''}`)) return;
                        purge.mutate(a.id, { onSuccess: () => toast(`Deleted “${a.title}” permanently`), onError: (e) => toast(errorMessage(e), 'error') });
                      }}>
                      Delete permanently
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <BinVersions versions={q.data?.versions ?? []} canAct={me.permissions.includes('asset.delete_versions')} />
      </div>
    </>
  );
}

/** Earlier versions of assets in the bin (1.2.3): Restore and Delete permanently, as for assets. */
function BinVersions({ versions, canAct }: { versions: BinVersion[]; canAct: boolean }) {
  const { restore, purge } = useBinVersionActions();
  if (!versions.length) return null;
  return (
    <section data-testid="bin-versions">
      <h2 className="bin-section-title">Earlier versions ({versions.length})</h2>
      <p className="faint" style={{ margin: '0 0 8px', fontSize: 12.5 }}>Versions deleted from an asset’s Versions tab or by Administration → Storage → Old versions. The asset and its current version stay in the library.</p>
      <TableScroll>
        <table className="table" aria-label="Deleted versions">
          <thead><tr><th>Version</th><th>Deleted</th><th>Purge date</th><th>Size</th><th aria-label="Actions" /></tr></thead>
          <tbody>
            {versions.map((v) => (
              <tr key={v.versionId} data-testid="bin-version" data-version-id={v.versionId}>
                <td className="wrap" style={{ minWidth: 200 }}><Link to={`/assets/${v.assetId}?tab=versions`} style={{ fontWeight: 550 }}>Version {v.number} of “{v.title}”</Link>
                  {v.originalFilename && <div className="faint break-any" style={{ fontSize: 12 }}>{v.originalFilename}</div>}</td>
                <td className="muted nowrap" title={dateTime(v.deletedAt)}>{relative(v.deletedAt)}{v.deletedBy ? ` by ${v.deletedBy}` : ''}</td>
                <td className="muted wrap" title={dateTime(v.purgeAfter)}>{v.purging ? 'Being purged' : relative(v.purgeAfter)}
                  {v.purgeError && <div style={{ color: 'var(--danger)', fontSize: 12 }}>{v.purgeError}</div>}</td>
                <td className="muted nowrap">{bytes(v.sizeBytes)}</td>
                <td className="actions"><div>
                  {canAct ? <>
                    <button className="btn sm" disabled={restore.isPending || v.purging} data-testid="bin-version-restore"
                      onClick={() => restore.mutate(v, { onSuccess: () => toast(`Restored version ${v.number} of “${v.title}”`), onError: (e) => toast(errorMessage(e), 'error') })}>
                      <RotateCcw size={13} /> Restore
                    </button>
                    <button className="btn ghost sm danger" disabled={purge.isPending} data-testid="bin-version-purge"
                      onClick={() => {
                        if (!window.confirm(`Delete version ${v.number} of “${v.title}” permanently? Its files PRISM manages are deleted; this cannot be undone.`)) return;
                        purge.mutate(v, { onSuccess: () => toast(`Deleted version ${v.number} permanently`), onError: (e) => toast(errorMessage(e), 'error') });
                      }}>Delete permanently</button>
                  </> : <span className="faint" style={{ fontSize: 12 }}>Needs Delete old versions</span>}
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableScroll>
    </section>
  );
}
